import React from 'react';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { P } from '@osmosis/tailwind-ui/components/Text';
import { textGray500 } from '@osmosis/tailwind-ui/styles/text';
import { LoggedOutProductLibraryContainsItem } from '../../types/library';
import { formattedUrl } from '../../lib/library/format';
import { useLanguageContext } from '../../context/LanguageProvider';
import LibraryTopicLink from './LibraryTopicLink';
import ScrollToElement from '@osmosis/nextjs-components/ScrollToElement';

type LibraryL5PageLoggedOutProps = {
  l1Id: string;
  l2Id: string;
  l3Id: string;
  l4Id: string;
  l5: LoggedOutProductLibraryContainsItem;
  targetId?: string;
};

/**
 * Component for rendering a logged-out view of a Level 5 library page.
 * @param {Object} props - The component props.
 * @param {string} props.l1Id - The ID of the Level 1 library item.
 * @param {string} props.l2Id - The ID of the Level 2 library item.
 * @param {string} props.l3Id - The ID of the Level 3 library item.
 * @param {string} props.l4Id - The ID of the Level 4 library item.
 * @param {LoggedOutProductLibraryContainsItem} props.l5 - The Level 5 library item.
 * @param {string} [props.targetId] - The ID of the target element to scroll to.
 * @returns {JSX.Element} The rendered component.
 */
const LibraryL5PageLoggedOut = ({
  l1Id,
  l2Id,
  l3Id,
  l4Id,
  l5,
  targetId,
}: LibraryL5PageLoggedOutProps) => {
  const { activeLanguage } = useLanguageContext();
  const elementId = encodeURI(`${l1Id}-${l2Id}-${l3Id}-${l4Id}-${l5.id}-L5`);
  const isTargetElement = targetId === elementId;

  return l5.type === 'level' ? (
    <Box className="flex flex-col gap-2">
      {isTargetElement && <ScrollToElement elementId={elementId} />}
      <P
        className={`${textGray500}`}
        id={`${l1Id}-${l2Id}-${l3Id}-${l4Id}-${l5.id}-L5`}
      >
        {l5.displayName || l5.name}
      </P>
      {l5?.contains?.map((l6) => (
        <LibraryTopicLink
          key={l6.name}
          item={l6}
          href={formattedUrl(l6.name, {
            language: activeLanguage,
          })}
        />
      ))}
    </Box>
  ) : (
    <LibraryTopicLink
      key={l5.name}
      item={l5}
      href={formattedUrl(l5.name, {
        language: activeLanguage,
      })}
    />
  );
};

export default LibraryL5PageLoggedOut;
